import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Pagination, CircularProgress, TextField, Button, MenuItem,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';

const typeColor = { sale: 'error', purchase: 'success', adjustment: 'warning', return: 'info', opening: 'default' };

const fmt = (n) => new Intl.NumberFormat('en-PK').format(+n || 0);

export default function StockMovementsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/inventory/movements', {
        params: { page, limit: 20, type: type || undefined, from: from || undefined, to: to || undefined },
      });
      setRows(data.data); setTotal(data.pagination?.total || 0);
    } finally { setLoading(false); }
  }, [page, type, from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Stock Movements</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField select size="small" label="Type" value={type} onChange={e => setType(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">All Types</MenuItem>
              {['sale', 'purchase', 'return', 'adjustment', 'opening'].map(t => (
                <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="To" type="date" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="outlined" onClick={() => { setType(''); setFrom(''); setTo(''); setPage(1); }}>Clear</Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Qty Change</TableCell>
                  <TableCell align="right">Before</TableCell>
                  <TableCell align="right">After</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.sku}</Typography>
                    </TableCell>
                    <TableCell><Chip label={r.movement_type} size="small" color={typeColor[r.movement_type] || 'default'} /></TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color={r.quantity_change > 0 ? 'success.main' : 'error.main'}>
                        {r.quantity_change > 0 ? '+' : ''}{fmt(r.quantity_change)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(r.quantity_before)}</TableCell>
                    <TableCell align="right">{fmt(r.quantity_after)}</TableCell>
                    <TableCell><Typography variant="caption">{r.reference_number || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{r.branch_name || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{new Date(r.created_at).toLocaleString('en-PK')}</Typography></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No movements found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 20)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
